import { useEffect, useState, useCallback } from 'react';
import apiClient from '../utils/api';

const NEAR_LIMIT_THRESHOLD = 80; // %

// Reads GET /api/usage/me and exposes helpers for upgrade nudges (Phase 6)
// and the Billing page usage meters (Phase 8).
export const useUsage = ({ autoLoad = true } = {}) => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/usage/me');
      setUsage(res.data);
      return res.data;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) refresh();
  }, [autoLoad, refresh]);

  // 'safe' < 80%, 'warning' 80-99%, 'critical' >= 100%
  const getQuotaStatus = (limitKey) => {
    if (!usage) return 'safe';
    const pct = usage.percentages?.[limitKey] ?? 0;
    if (pct >= 100) return 'critical';
    if (pct >= NEAR_LIMIT_THRESHOLD) return 'warning';
    return 'safe';
  };

  // Returns array of limit types currently >= 80%
  const getNearLimitQuotas = () => {
    if (!usage) return [];
    const keys = ['forms', 'submissions_this_month', 'signed_docs_this_month', 'storage'];
    return keys.filter((k) => (usage.percentages?.[k] ?? 0) >= NEAR_LIMIT_THRESHOLD);
  };

  const isEnterprise = usage?.plan === 'enterprise';

  return {
    usage,
    loading,
    error,
    refresh,
    getQuotaStatus,
    getNearLimitQuotas,
    isEnterprise,
    NEAR_LIMIT_THRESHOLD,
  };
};
